import { DunningBlockReason, OpenPositionType } from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { useOpenPositionDunningBlockCreateMutation } from "@/common/api/open-position/use-open-position-dunning-block-create.mutation";
import { useOpenPositionDunningBlockDeleteMutation } from "@/common/api/open-position/use-open-position-dunning-block-delete.mutation";
import type { OpenPosition } from "@/common/api/open-position/use-open-position-search.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import type { RowActionHelpers } from "@/components/data-table/predefined/_components/base-predefined-table.types";
import type { DataTableRow } from "@/components/data-table/types";
import { RowAction, RowActionVariant } from "@/components/ui/table-row-actions";
import { FiaTrackingEvent, FiaTrackingSource, trackFiaEvent } from "@/modules/fia/fia.tracking";
import { LockIcon, LockOpenIcon } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { DunningBlockDialog } from "../_components/dunning-block-dialog";

interface DunningBlockDialogState {
  position: OpenPosition;
  reason: DunningBlockReason;
  helpers: RowActionHelpers;
}

const DEFAULT_DUNNING_BLOCK_REASON = DunningBlockReason.DebtorClarification;

export function useDunningBlockActions(source: FiaTrackingSource): {
  buildRowActions: (row: DataTableRow<OpenPosition>, helpers: RowActionHelpers) => RowAction[];
  dialog: ReactNode;
} {
  const { t } = useTranslation(["fia"]);
  const ability = useAbility();
  const canManageDunningBlocks = ability.can("manage", "OpenPositionDunningBlock");

  const [dialogState, setDialogState] = useState<DunningBlockDialogState | null>(null);
  const { mutateAsync: createDunningBlock, isPending: isCreating } = useOpenPositionDunningBlockCreateMutation();
  const { mutateAsync: deleteDunningBlock, isPending: isDeleting } = useOpenPositionDunningBlockDeleteMutation();
  const isMutating = isCreating || isDeleting;

  const closeDialog = useCallback(() => setDialogState(null), []);

  const handleConfirm = useCallback(async () => {
    if (!dialogState) return;
    const { position, reason, helpers } = dialogState;
    trackFiaEvent(FiaTrackingEvent.DunningBlockSet, { source });

    await toastMutationStatus({
      mutationFn: () => createDunningBlock({ openPositionUuid: position.uuid, body: { reason } }),
      successMessage: t("fia:DunningBlockSet"),
      errorMessage: t("fia:ErrorSettingDunningBlock"),
      customErrors: { 409: t("fia:OpenPositionAlreadyBlocked") },
      onSuccess: closeDialog,
    });
    // A queue error can happen after saving; recount even when the HTTP request failed.
    helpers.invalidateCount();
  }, [dialogState, createDunningBlock, closeDialog, source, t]);

  const handleRemove = useCallback(
    async (position: OpenPosition, helpers: RowActionHelpers) => {
      trackFiaEvent(FiaTrackingEvent.DunningBlockRemove, { source });

      await toastMutationStatus({
        mutationFn: () => deleteDunningBlock({ openPositionUuid: position.uuid }),
        successMessage: t("fia:DunningBlockRemoved"),
        errorMessage: t("fia:ErrorRemovingDunningBlock"),
      });
      helpers.invalidateCount();
    },
    [deleteDunningBlock, source, t],
  );

  const buildRowActions = useCallback(
    (row: DataTableRow<OpenPosition>, helpers: RowActionHelpers): RowAction[] => {
      if (!canManageDunningBlocks) return [];
      const position = row.original;

      if (position.dunningBlock) {
        return [
          {
            label: t("fia:RemoveDunningBlock"),
            icon: <LockOpenIcon className="h-4 w-4" />,
            variant: RowActionVariant.Success,
            disabled: isMutating,
            onClick: () => handleRemove(position, helpers),
          },
        ];
      }

      if (position.type !== OpenPositionType.Invoice) return [];

      return [
        {
          label: t("fia:SetDunningBlock"),
          icon: <LockIcon className="h-4 w-4" />,
          variant: RowActionVariant.Warning,
          disabled: isMutating,
          onClick: () => setDialogState({ position, reason: DEFAULT_DUNNING_BLOCK_REASON, helpers }),
        },
      ];
    },
    [canManageDunningBlocks, handleRemove, isMutating, t],
  );

  const dialog = dialogState ? (
    <DunningBlockDialog
      open
      reason={dialogState.reason}
      onReasonChange={(reason) => setDialogState((state) => (state ? { ...state, reason } : state))}
      onConfirm={handleConfirm}
      onCancel={closeDialog}
      isLoading={isCreating}
    />
  ) : null;

  return { buildRowActions, dialog };
}
